import { useState, useEffect, useLayoutEffect, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { usePricingData } from '../context/ApiContext'
import {
  HiHome,
  HiCog,
  HiUsers,
  HiChartBar,
  HiCheckCircle,
  HiSun,
  HiMoon,
  HiChevronLeft,
  HiChevronRight,
  HiLogout,
  HiDocumentText,
  HiCurrencyDollar,
  HiUserCircle,
  HiCollection,
  HiLocationMarker,
  HiPencilAlt,
  HiOfficeBuilding,
} from 'react-icons/hi'
import logoIcon from '../assets/streamline-logo-fullcolour.svg'
import logoText from '../assets/streamline-logo-fullcolour-textonly.svg'
import './Sidebar.css'

function StateFocusIcon({ className }) {
  return (
    <svg className={className} width="1em" height="1em" viewBox="0 0 20 20" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
      <path d="M5.5 4a2.5 2.5 0 0 0-2.5 2.5c0 1.7 2.5 4.5 2.5 4.5s2.5-2.8 2.5-4.5A2.5 2.5 0 0 0 5.5 4zm0 3.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" opacity=".45" />
      <path d="M14.5 3a2.5 2.5 0 0 0-2.5 2.5c0 1.7 2.5 4.5 2.5 4.5s2.5-2.8 2.5-4.5A2.5 2.5 0 0 0 14.5 3zm0 3.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z" opacity=".45" />
      <path d="M10 8a3 3 0 0 0-3 3c0 2.1 3 5.5 3 5.5s3-3.4 3-5.5a3 3 0 0 0-3-3zm0 4.25a1.25 1.25 0 1 1 0-2.5 1.25 1.25 0 0 1 0 2.5z" />
    </svg>
  )
}

function BubblePlotIcon({ className }) {
  return (
    <svg className={className} width="1em" height="1em" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5">
      <circle cx="4" cy="4" r="2.5" />
      <circle cx="10" cy="8" r="1.5" />
      <circle cx="6" cy="11" r="1.5" />
    </svg>
  )
}

const SIDEBAR_STORAGE_KEY = 'sidebarCollapsed'

const DASHBOARD_ITEMS = [
  { path: '/overview', label: 'Overview', icon: HiChartBar },
  { path: '/tactical-focus', label: 'Tactical Focus', icon: HiCollection },
]

const STATIC_SECTIONS = [
  {
    section: 'Pricing',
    items: [
      { path: '/pricing-actions', label: 'Actions', icon: HiCheckCircle },
      { path: '/pricing-rules', label: 'Rules', icon: HiPencilAlt },
      { path: '/ecr', label: 'ECR', icon: HiCurrencyDollar },
    ],
  },
  {
    section: 'Insights',
    items: [
      { path: '/kss-focus', label: 'KSS Focus', icon: HiHome },
      { path: '/state-focus', label: 'State Focus', icon: StateFocusIcon },
      { path: '/site-focus', label: 'Site Focus', icon: HiLocationMarker },
      { path: '/opportunities', label: 'Opportunities', icon: HiChartBar },
      { path: '/bubble-plots', label: 'Bubble Plots', icon: BubblePlotIcon },
      { path: '/reports', label: 'Reports', icon: HiDocumentText },
    ],
  },
  {
    section: 'Settings',
    items: [
      { path: '/competitors', label: 'Competitors', icon: HiOfficeBuilding },
      { path: '/configuration', label: 'Configuration', icon: HiCog },
      { path: '/manage-account', label: 'Manage Account', icon: HiUserCircle },
    ],
    adminItems: [
      { path: '/users', label: 'Users', icon: HiUsers },
    ],
  },
]

function getNavSections() {
  return [
    { section: 'Views', items: DASHBOARD_ITEMS },
    ...STATIC_SECTIONS,
  ]
}

export default function Sidebar() {
  const { theme, toggleTheme } = useTheme()
  const { user, signOut, hasAdminPermissions } = useAuth()
  const { priceChanges } = usePricingData()
  const navigate = useNavigate()
  const location = useLocation()
  const [collapsed, setCollapsed] = useState(() =>
    window.innerWidth < 768 || localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true'
  )
  const navSections = getNavSections()

  const navBadges = useMemo(() => {
    const actionsCount = priceChanges?.size || 0
    let rulesCount = 0
    try {
      const stored = localStorage.getItem('kss-price-decisions')
      if (stored) {
        const decisions = JSON.parse(stored)
        for (const d of Object.values(decisions)) {
          if (d === 'override' || d === 'promotion' || d === 'snooze') rulesCount++
        }
      }
    } catch { /* ignore */ }
    return {
      '/pricing-actions': actionsCount,
      '/pricing-rules': rulesCount,
    }
  }, [priceChanges])

  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--sidebar-width', collapsed ? '64px' : '220px')
    localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed)
  }, [collapsed])

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)')
    const handler = (e) => { if (e.matches) setCollapsed(true) }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  const toggleCollapsed = () => setCollapsed(prev => !prev)

  const navigateTo = (path) => {
    navigate(path)
    window.scrollTo(0, 0)
  }

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname === path || location.pathname.startsWith(path + '/')
  }

  const displayName = user?.name || 'User'
  const displayEmail = user?.email || ''
  const initials = displayName.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)
  const roleLabel = hasAdminPermissions ? 'Admin' : 'User'

  return (
    <aside className={`app-sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-logo">
        <img src={logoIcon} alt="" className="logo-icon-img" aria-hidden="true" />
        <img src={logoText} alt="Streamline" className="logo-text-img" />
      </div>

      <nav className="sidebar-nav">
        {navSections.map((section, si) => (
          <div key={si} className="nav-section">
            {!collapsed && (
              <div className="nav-section-divider">
                <span className="nav-section-label">{section.section}</span>
              </div>
            )}
            {section.items.map(item => {
              const badge = navBadges[item.path]
              return (
                <button
                  key={item.path}
                  type="button"
                  className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                  onClick={() => navigateTo(item.path)}
                  title={item.label}
                  aria-current={isActive(item.path) ? 'page' : undefined}
                >
                  <item.icon className="nav-icon" />
                  <span className="nav-label">{item.label}</span>
                  {badge > 0 && <span className="nav-badge">{badge}</span>}
                </button>
              )
            })}
            {hasAdminPermissions && section.adminItems?.map(item => (
              <button
                key={item.path}
                type="button"
                className={`nav-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => navigateTo(item.path)}
                title={item.label}
                aria-current={isActive(item.path) ? 'page' : undefined}
              >
                <item.icon className="nav-icon" />
                <span className="nav-label">{item.label}</span>
              </button>
            ))}
          </div>
        ))}
      </nav>

      <div className="sidebar-bottom">
        <div className="sidebar-user-card">
          <div className="user-avatar" title={displayName}>{initials}</div>
          {!collapsed && (
            <div className="user-card-details">
              <div className="user-card-row">
                <span className="user-card-name">{displayName}</span>
                <span className={`user-card-role ${hasAdminPermissions ? 'admin' : ''}`}>{roleLabel}</span>
              </div>
              <span className="user-card-email">{displayEmail}</span>
            </div>
          )}
        </div>

        <div className="sidebar-controls">
          <button
            type="button"
            className="sidebar-ctrl-btn"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            aria-label={theme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {theme === 'dark' ? <HiSun className="ctrl-icon" /> : <HiMoon className="ctrl-icon" />}
          </button>
          {user && !collapsed && (
            <button type="button" className="sidebar-ctrl-btn" onClick={signOut} title="Sign Out" aria-label="Sign Out">
              <HiLogout className="ctrl-icon" />
            </button>
          )}
          <button
            type="button"
            className="sidebar-ctrl-btn collapse-btn"
            onClick={toggleCollapsed}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <HiChevronRight className="ctrl-icon" /> : <HiChevronLeft className="ctrl-icon" />}
          </button>
        </div>
      </div>
    </aside>
  )
}
